// OP-3605 — A contagem (ver ESL1; contar ESL7; aprovar ESL8).
//
// Duas formas de contar, porque são dois tipos de item: consumível se digita,
// peça se marca uma a uma. Marcar é achar — o que ninguém marcou NÃO foi
// encontrado, e vira extravio na aprovação.
//
// E quem contou não aprova: a segregação é a razão de o inventário existir.
import { FC, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueInventario } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import {
  aprovarEstoqueInventario, contarEstoqueInventario, descartarEstoqueInventario, fecharEstoqueInventario,
  marcarPecaEstoqueInventario, reabrirEstoqueInventario,
} from '../../../../Service/estoque'
import { InventarioLinha } from '../../../../types/estoque'
import {
  BD, Button, Card, DetailLayout, Field, Input, ListaCartoes, PageSubtitle, PageTitle, SEVERIDADE, SoCelular, SoDesktop, Spinner, StatusBadge, TabelaWrap, Table,
} from '../../styles'
import { fmtDateTime } from '../../statusUi'
import { LinhaQuantidade, ListaMarcacao } from '../Contagem'
import { AcaoDecisao, Decisao } from '../Decisao'
import { tratarErro } from '../erros'
import { RetornoLeitura, acharPorCodigo } from '../leitura'
import { LeitorContinuo } from '../LeitorContinuo'
import {
  STATUS_INVENTARIO_UI, contadasNaLinha, diferencaDaLinha, emBranco, foiEncontrada,
  impedimentoAprovarInventario, impedimentoDescartarInventario, impedimentoFecharContagem, impedimentoReabrir,
  naoEncontradasNaLinha, quantidadeContadaValida,
} from './regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const Bloco: FC<{ titulo: string; children: any }> = ({ titulo, children }) => (
  <Card style={{ padding: 16, marginBottom: 16 }}><div style={{ fontWeight: 600, marginBottom: 8 }}>{titulo}</div>{children}</Card>
)

export const EstoqueInventario: FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const idNum = Number(id) || null
  const { pode, usuarioId } = usePerfilEstoque()
  const podeContar = pode('INVENTARIAR')
  const podeAprovar = pode('APROVAR_INVENTARIO')
  const { data: inv, isLoading, isError, isUnauthorized, mutate } = useEstoqueInventario(idNum)
  const [busy, setBusy] = useState(false)
  const [digitado, setDigitado] = useState<Record<number, string>>({})
  const [motivo, setMotivo] = useState('')
  const [confirmandoDescarte, setConfirmandoDescarte] = useState(false)
  const [motivoDescarte, setMotivoDescarte] = useState('')
  // O 409 do corte não é erro de formulário: é "o mundo mudou". Fica na tela
  // com o caminho de saída — reabrir —, em vez de sumir num toast.
  const [desatualizado, setDesatualizado] = useState<string | null>(null)
  const marcadasNaLeitura = useRef<Set<number>>(new Set())

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />
  if (isLoading) return <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>
  if (isError || !inv) {
    return (
      <>
        <PageTitle>Inventário</PageTitle>
        <Card><div style={{ padding: 24, color: BD.grayText }}>
          Contagem não encontrada ou fora do seu escopo. <Button $variant="secondary" onClick={() => navigate('/interno/estoque/inventarios')}>Voltar</Button>
        </div></Card>
      </>
    )
  }

  const executar = async (acao: () => Promise<any>, ok: string, limpar?: () => void) => {
    setBusy(true)
    try {
      const res = await acao()
      toast.success(ok)
      if (limpar) limpar()
      setDesatualizado(null)
      await mutate()
      return res
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return null }
      if (e.codigo === 'INVENTARIO_DESATUALIZADO') { setDesatualizado(e.texto); await mutate(); return null }
      toast.error(e.texto)
      if (e.tipo === 'estado') await mutate()
      return null
    } finally {
      setBusy(false)
    }
  }

  const ui = STATUS_INVENTARIO_UI[inv.status] ?? { bg: '#F3F4F6', color: '#374151', label: inv.status }
  const emContagem = inv.status === 'EM_CONTAGEM'
  const impFechar = impedimentoFecharContagem(inv.linhas)
  const impAprovar = impedimentoAprovarInventario({
    status: inv.status, contadoPorId: inv.contadoPorId, participantesIds: inv.participantesIds,
    usuarioId, podeAprovar, motivo,
  })
  const impReabrir = impedimentoReabrir(inv.status)
  const aberta = inv.status === 'EM_CONTAGEM' || inv.status === 'AGUARDANDO_APROVACAO'
  const impDescartar = impedimentoDescartarInventario({
    status: inv.status, contadoPorId: inv.contadoPorId, participantesIds: inv.participantesIds,
    usuarioId, podeDescartar: podeAprovar, motivo: motivoDescarte,
  })
  const totalNaoMarcadas = inv.linhas.reduce((a, l) => a + naoEncontradasNaLinha(l), 0)

  /** O que a linha mostra é o que o Registrar envia — inclusive o já gravado. */
  const valorDigitado = (l: InventarioLinha) => digitado[l.itemId] ?? (l.contado == null ? '' : String(l.contado))

  const contar = (l: InventarioLinha) => {
    const v = valorDigitado(l)
    if (!quantidadeContadaValida(v)) { toast.error('Informe um número inteiro. Em branco não é zero.'); return }
    return executar(() => contarEstoqueInventario({ id: inv.id, itemId: l.itemId, quantidade: Number(v) }), `${l.nome}: contagem registrada`)
  }

  const linhaQuantidade = (l: InventarioLinha) => {
    const d = diferencaDaLinha(l)
    return (
      <tr key={l.id}>
        <td>{l.nome}{l.codigo ? <span style={{ fontSize: 11, color: BD.grayText }}> · {l.codigo}</span> : null}</td>
        <td style={{ textAlign: 'right' }}>{l.esperado}</td>
        <td>
          {emContagem && podeContar ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Input type="number" min={0} style={{ width: 100 }}
                     value={valorDigitado(l)}
                     onChange={(e) => setDigitado((q) => ({ ...q, [l.itemId]: e.target.value }))} />
              <Button $variant="secondary" disabled={busy} onClick={() => contar(l)}>Registrar</Button>
            </div>
          ) : (
            <span>{l.contado == null ? <span style={{ color: BD.grayText }}>em branco</span> : l.contado}</span>
          )}
        </td>
        <td style={{ textAlign: 'right', fontWeight: d ? 600 : 400, color: d ? SEVERIDADE.atencao.texto : undefined }}>
          {d == null ? '—' : d > 0 ? `+${d}` : d}
        </td>
        <td style={{ fontSize: 12, color: BD.grayText }}>{l.contadoEm ? fmtDateTime(l.contadoEm) : ''}</td>
      </tr>
    )
  }

  const linhaSerial = (l: InventarioLinha) => {
    const achadas = contadasNaLinha(l)
    const d = diferencaDaLinha(l)
    return (
      <div key={l.id} style={{ borderTop: `1px solid ${BD.grayBorder}`, padding: '12px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div><b>{l.nome}</b> <span style={{ fontSize: 12, color: BD.grayText }}>esperado {l.esperado} · achadas {achadas}</span></div>
          {d != null && <span style={{ color: SEVERIDADE.atencao.texto, fontWeight: 600 }}>{d > 0 ? `+${d}` : d}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {(l.unidades ?? []).map((u) => {
            const achou = foiEncontrada(u)
            return (
              <Button key={u.unidadeId} $variant={achou ? 'primary' : 'secondary'} disabled={busy || !emContagem || !podeContar}
                      title={u.marcadaEm ? `marcada ${fmtDateTime(u.marcadaEm)}` : 'ninguém marcou'}
                      onClick={() => executar(() => marcarPecaEstoqueInventario({ id: inv.id, unidadeId: u.unidadeId, encontrada: !achou }), achou ? 'Peça desmarcada' : 'Peça encontrada')}>
                {achou ? '✓ ' : ''}{u.serial}
              </Button>
            )
          })}
          {(l.unidades ?? []).length === 0 && <span style={{ fontSize: 13, color: BD.grayText }}>Nenhuma peça deste item na praça.</span>}
        </div>
      </div>
    )
  }

  const porQuantidade = inv.linhas.filter((l) => l.controle === 'QUANTIDADE')
  const porPeca = inv.linhas.filter((l) => l.controle === 'SERIAL')
  // O que falta não é uma coisa só: consumível em branco trava o fechamento,
  // peça sem marca vira extravio na aprovação. Contar os dois juntos mentiria
  // numa contagem só de peças, onde `emBranco` é sempre falso.
  const consumiveisContados = inv.linhas.filter((l) => l.controle === 'QUANTIDADE' && !emBranco(l)).length
  const totalPecas = inv.linhas
    .filter((l) => l.controle === 'SERIAL')
    .reduce((soma, l) => soma + (l.unidades ?? []).length, 0)
  const pecasMarcadas = inv.linhas
    .filter((l) => l.controle === 'SERIAL')
    .reduce((soma, l) => soma + contadasNaLinha(l), 0)

  const pecasDaContagem = porPeca.flatMap((l) => (l.unidades ?? []).map((u) => ({ u, l })))

  const lerPeca = async (codigo: string): Promise<RetornoLeitura> => {
    const achada = acharPorCodigo(pecasDaContagem, codigo, ({ u }) => [u.serial, u.patrimonio])
    if (!achada) return { texto: `${codigo} não está nesta contagem.`, tom: 'erro' }
    const { u, l } = achada
    if (foiEncontrada(u) || marcadasNaLeitura.current.has(u.unidadeId)) return { texto: `${u.serial} já estava marcada.`, tom: 'aviso' }
    marcadasNaLeitura.current.add(u.unidadeId)
    try {
      await marcarPecaEstoqueInventario({ id: inv.id, unidadeId: u.unidadeId, encontrada: true })
      return { texto: `✓ ${u.serial} · ${l.nome}`, tom: 'ok' }
    } catch (err) {
      marcadasNaLeitura.current.delete(u.unidadeId)
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) navigate('/auth')
      if (e.codigo === 'INVENTARIO_DESATUALIZADO') setDesatualizado(e.texto)
      if (e.codigo === 'INVENTARIO_DESATUALIZADO' || e.tipo === 'estado') mutate()
      return { texto: e.texto, tom: 'erro' }
    }
  }

  const cartaoQuantidade = (l: InventarioLinha) => {
    const d = diferencaDaLinha(l)
    const editavel = emContagem && podeContar
    return (
      <LinhaQuantidade
        key={l.id}
        titulo={l.nome}
        referencia={`esperado ${l.esperado}`}
        valor={valorDigitado(l)}
        aoMudar={(v) => setDigitado((q) => ({ ...q, [l.itemId]: v }))}
        rotulo={`contado de ${l.nome}`}
        disabled={busy}
        somenteLeitura={!editavel}
        acao={<Button $variant="secondary" disabled={busy} onClick={() => contar(l)}>Registrar</Button>}
        alerta={editavel && d != null && d !== 0}
        nota={editavel
          ? (l.contado == null
            ? 'Em branco não é zero: sem número a contagem não fecha.'
            : `registrado ${l.contado}${d ? ` · diferença ${d > 0 ? `+${d}` : d}` : ' · confere'}`)
          : (
            <>
              {l.contado == null ? 'em branco' : `contado ${l.contado}`}
              {d ? <b style={{ color: SEVERIDADE.atencao.texto }}> · {d > 0 ? `+${d}` : d}</b> : null}
            </>
          )}
      />
    )
  }

  const cartaoPeca = (l: InventarioLinha) => (
    <Card key={l.id} style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <b style={{ flexGrow: 1, fontSize: 15 }}>{l.nome}</b>
        <span style={{ fontSize: 12, color: BD.grayText }}>esperado {l.esperado} · achadas {contadasNaLinha(l)}</span>
      </div>
      <div style={{ marginTop: 6 }}>
        <ListaMarcacao
          itens={(l.unidades ?? []).map((u) => ({
            id: u.unidadeId,
            titulo: u.serial,
            detalhe: u.marcadaEm ? `marcada ${fmtDateTime(u.marcadaEm)}` : 'ninguém marcou',
            marcada: foiEncontrada(u),
          }))}
          disabled={busy || !emContagem || !podeContar}
          vazio="Nenhuma peça deste item na praça."
          aoAlternar={(unidadeId, encontrada) => executar(
            () => marcarPecaEstoqueInventario({ id: inv.id, unidadeId, encontrada }),
            encontrada ? 'Peça encontrada' : 'Peça desmarcada',
          )}
        />
      </div>
    </Card>
  )

  const fechar = async () => {
    const res = await executar(() => fecharEstoqueInventario(inv.id), 'Contagem fechada')
    const d = res?.data?.data
    if (d && Number(d.divergencias) > 0) toast.info(`${d.divergencias} divergência(s): a contagem foi para aprovação.`)
  }

  const aprovar = async () => {
    const res = await executar(() => aprovarEstoqueInventario(inv.id, motivo.trim()), 'Ajuste aprovado', () => setMotivo(''))
    const d = res?.data?.data
    if (d) toast.info(`${d.ajustes ?? 0} ajuste(s), ${d.extravios ?? 0} extravio(s)${Number(d.sucatasNaoEncontradas) > 0 ? `, ${d.sucatasNaoEncontradas} já na fila de baixa` : ''}.`)
  }

  // A ação do estado não some por falta de alçada: ela aparece desabilitada
  // dizendo o que a destrava. Botão ausente faz a pessoa procurar o que não
  // existe; botão desligado com motivo encerra a dúvida.
  const impFecharNaTela = podeContar ? impFechar : 'Fechar a contagem exige a permissão "Inventariar".'

  const principal: AcaoDecisao | null = emContagem
    ? { rotulo: 'Fechar contagem', rodar: fechar, impedimento: impFecharNaTela }
    : inv.status === 'AGUARDANDO_APROVACAO'
      ? { rotulo: 'Aprovar ajuste', rodar: aprovar, impedimento: impAprovar }
      : null

  const desarmarDescarte = () => { setConfirmandoDescarte(false); setMotivoDescarte('') }
  const reabrir: AcaoDecisao[] = inv.status === 'AGUARDANDO_APROVACAO' && podeContar
    ? [{
      rotulo: 'Reabrir contagem',
      rodar: () => executar(() => reabrirEstoqueInventario(inv.id), 'Contagem reaberta', () => setMotivo('')),
      impedimento: impReabrir,
      variante: 'secondary' as const,
    }]
    : []
  const descartar: AcaoDecisao[] = aberta && podeAprovar
    ? (confirmandoDescarte
      ? [
        { rotulo: 'Voltar', rodar: desarmarDescarte, variante: 'secondary' as const },
        {
          rotulo: `Confirmar o descarte de ${inv.numero}`,
          rodar: () => executar(() => descartarEstoqueInventario(inv.id, motivoDescarte.trim()), 'Contagem descartada', desarmarDescarte),
          impedimento: impDescartar,
          variante: 'secondary' as const,
        },
      ]
      : [{ rotulo: 'Descartar contagem', rodar: () => setConfirmandoDescarte(true), variante: 'secondary' as const }])
    : []
  const secundarias: AcaoDecisao[] = confirmandoDescarte ? descartar : reabrir.concat(descartar)
  const campoMotivoDescarte = confirmandoDescarte ? (
    <>
      <div style={{ fontSize: 12, color: BD.grayText, marginBottom: 8 }}>
        Descartar encerra a contagem sem lançar nada no razão e libera a praça para uma contagem nova.
      </div>
      <Field style={{ marginBottom: 8 }}>
        <label>Motivo do descarte</label>
        <Input aria-label="Motivo do descarte" value={motivoDescarte} maxLength={400} onChange={(e) => setMotivoDescarte(e.target.value)} />
      </Field>
    </>
  ) : null

  return (
    <DetailLayout $contido>
      <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>{inv.numero} <StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge></PageTitle>
          <SoDesktop>
            <PageSubtitle>
              {inv.localNome} · aberta {fmtDateTime(inv.abertoEm)} por {inv.contadoPorNome}
              {inv.fechadoEm && <> · fechada {fmtDateTime(inv.fechadoEm)}</>}
              {inv.aprovadoPorNome && <> · aprovada por {inv.aprovadoPorNome} {fmtDateTime(inv.aprovadoEm)}</>}
              {inv.descartadoPorNome && <> · descartada por {inv.descartadoPorNome} {fmtDateTime(inv.descartadoEm)}</>}
              {inv.motivoAjuste && <div style={{ marginTop: 4 }}>{inv.motivoAjuste}</div>}
              {inv.motivoDescarte && <div style={{ marginTop: 4 }}>Motivo do descarte: {inv.motivoDescarte}</div>}
            </PageSubtitle>
          </SoDesktop>
          <SoCelular>
            <PageSubtitle>
              {inv.localNome} · aberta por {inv.contadoPorNome}
              {inv.descartadoPorNome && <> · descartada por {inv.descartadoPorNome}</>}
              {inv.motivoAjuste && <div style={{ marginTop: 4 }}>{inv.motivoAjuste}</div>}
              {inv.motivoDescarte && <div style={{ marginTop: 4 }}>Motivo do descarte: {inv.motivoDescarte}</div>}
            </PageSubtitle>
          </SoCelular>
        </div>
        <Button $variant="secondary" onClick={() => navigate('/interno/estoque/inventarios')}>‹ Inventários</Button>
      </div>

      {desatualizado && (
        <Card style={{ padding: 16, marginBottom: 16, borderLeft: `4px solid ${BD.red}` }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>A praça se moveu durante a contagem</div>
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
            {desatualizado} O ajuste não foi lançado — nada mudou no estoque. Reabrir refaz a foto do esperado e recomeça a contagem: os consumíveis voltam a branco e todas as peças ficam desmarcadas.
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {podeContar && (
              <Button disabled={busy || !!impReabrir} onClick={() => executar(() => reabrirEstoqueInventario(inv.id), 'Contagem reaberta', () => setMotivo(''))}>Reabrir contagem</Button>
            )}
            <Button $variant="secondary" onClick={() => setDesatualizado(null)}>Fechar aviso</Button>
          </div>
        </Card>
      )}

      <SoCelular>
        <Card style={{ padding: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
            <b>Contagem</b>
            <span style={{ fontSize: 12, color: BD.grayText }}>
              {porQuantidade.length > 0 ? `${consumiveisContados} de ${porQuantidade.length} consumíveis` : `${totalPecas} ${totalPecas === 1 ? 'peça' : 'peças'}`}
            </span>
          </div>
          {porQuantidade.length > 0 && (
            <div style={{ marginTop: 8, height: 8, borderRadius: 4, background: BD.grayBorder, overflow: 'hidden' }}>
              <div style={{ width: `${Math.round((consumiveisContados / porQuantidade.length) * 100)}%`, height: 8, background: BD.red }} />
            </div>
          )}
          {totalPecas > 0 && (
            <div style={{ marginTop: 8, fontSize: 13, color: BD.grayText }}>
              {pecasMarcadas} de {totalPecas} {totalPecas === 1 ? 'peça marcada' : 'peças marcadas'}
            </div>
          )}
          {emContagem && totalNaoMarcadas > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: BD.grayText }}>
              {totalNaoMarcadas} {totalNaoMarcadas === 1 ? 'peça está' : 'peças estão'} sem marca e {totalNaoMarcadas === 1 ? 'viraria extravio' : 'virariam extravio'} na aprovação.
            </div>
          )}
        </Card>
      </SoCelular>

      {porQuantidade.length > 0 && (
        <Bloco titulo={`Consumíveis · ${porQuantidade.length}`}>
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>Digite o que existe na prateleira. <b>Em branco não é zero</b>: linha sem número impede o fechamento.</div>
          <TabelaWrap>
            <Table>
              <thead><tr><th>Item</th><th style={{ textAlign: 'right' }}>Esperado</th><th>Contado</th><th style={{ textAlign: 'right' }}>Diferença</th><th>Quando</th></tr></thead>
              <tbody>{porQuantidade.map(linhaQuantidade)}</tbody>
            </Table>
          </TabelaWrap>
          <ListaCartoes>{porQuantidade.map(cartaoQuantidade)}</ListaCartoes>
        </Bloco>
      )}

      {porPeca.length > 0 && (
        <Bloco titulo={`Peças · ${porPeca.length} ${porPeca.length === 1 ? 'item' : 'itens'}`}>
          <div style={{ fontSize: 13, color: BD.grayText }}>
            Marque o que você <b>achou</b>. Peça não marcada conta como ausente — não existe "marcar como faltando".
            {emContagem && totalNaoMarcadas > 0 && <> Hoje {totalNaoMarcadas} {totalNaoMarcadas === 1 ? 'peça está' : 'peças estão'} sem marca e {totalNaoMarcadas === 1 ? 'viraria extravio' : 'virariam extravio'} na aprovação.</>}
          </div>
          {emContagem && podeContar && (
            <div style={{ marginTop: 8 }}>
              <LeitorContinuo
                titulo={`Contagem ${inv.numero}`}
                aoLer={lerPeca}
                aoAbrir={() => marcadasNaLeitura.current.clear()}
                aoFechar={() => mutate()}
                disabled={busy}
              />
            </div>
          )}
          <SoDesktop>{porPeca.map(linhaSerial)}</SoDesktop>
          <ListaCartoes>{porPeca.map(cartaoPeca)}</ListaCartoes>
        </Bloco>
      )}

      {inv.divergencias.length > 0 && (
        <Bloco titulo={`Divergências · ${inv.divergencias.length}`}>
          <TabelaWrap>
            <Table>
              <thead><tr><th>Item</th><th style={{ textAlign: 'right' }}>Esperado</th><th style={{ textAlign: 'right' }}>Contado</th><th style={{ textAlign: 'right' }}>Diferença</th></tr></thead>
              <tbody>
                {inv.divergencias.map((d) => (
                  <tr key={d.itemId}>
                    <td>{d.nome}</td>
                    <td style={{ textAlign: 'right' }}>{d.esperado}</td>
                    <td style={{ textAlign: 'right' }}>{d.contado}</td>
                    <td style={{ textAlign: 'right', color: SEVERIDADE.atencao.texto, fontWeight: 600 }}>{d.diferenca > 0 ? `+${d.diferenca}` : d.diferenca}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TabelaWrap>
          <ListaCartoes>
            {inv.divergencias.map((d) => (
              <Card key={d.itemId} style={{ padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <b style={{ flexGrow: 1, fontSize: 15 }}>{d.nome}</b>
                  <span style={{ fontSize: 16, fontWeight: 700, color: SEVERIDADE.atencao.texto }}>{d.diferenca > 0 ? `+${d.diferenca}` : d.diferenca}</span>
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: BD.grayText }}>esperado {d.esperado} · contado {d.contado}</div>
              </Card>
            ))}
          </ListaCartoes>
        </Bloco>
      )}

      {emContagem && (
        <Bloco titulo="Fechar contagem">
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>Sem divergência, a contagem fecha aqui mesmo. Com divergência, ela vai para aprovação — e quem contou não aprova.</div>
          {impFecharNaTela && <div style={{ fontSize: 13, color: BD.grayText }}>{impFecharNaTela}</div>}
        </Bloco>
      )}

      {inv.status === 'AGUARDANDO_APROVACAO' && (
        <Bloco titulo="Aprovar o ajuste">
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
            Aprovar lança o ajuste no razão: a diferença de consumível vira AJUSTE e cada peça não encontrada vira EXTRAVIO. Quem contou — quem abriu, digitou ou marcou qualquer coisa — não aprova.
            {' '}A contagem foi aberta por <b>{inv.contadoPorNome}</b>.
          </div>
          <Field><label>Motivo do ajuste</label><Input value={motivo} maxLength={400} onChange={(e) => setMotivo(e.target.value)} /></Field>
          {impAprovar && <div style={{ fontSize: 13, color: BD.grayText, marginTop: 12 }}>{impAprovar}</div>}
        </Bloco>
      )}

      </div>

      <Decisao
        estado={ui}
        desde={inv.descartadoEm ? `descartada ${fmtDateTime(inv.descartadoEm)}` : inv.fechadoEm ? `fechada ${fmtDateTime(inv.fechadoEm)}` : `aberta ${fmtDateTime(inv.abertoEm)}`}
        aviso={inv.status === 'AGUARDANDO_APROVACAO'
          ? <>Aprovar lança o ajuste no razão: a diferença vira AJUSTE e cada peça não encontrada vira EXTRAVIO. Quem contou não aprova — esta contagem foi aberta por <b>{inv.contadoPorNome}</b>.</>
          : null}
        principal={principal}
        secundarias={secundarias}
        pendencia={emContagem ? 'Item em branco não é zero: sem número a contagem não fecha.' : null}
        antesDasSaidas={campoMotivoDescarte}
        busy={busy}
        aoFechar={desarmarDescarte}
      />
    </DetailLayout>
  )
}

export default EstoqueInventario
